/**
 * F14.2 — INVARIANTE DE IDENTIDAD DE LA SINCRONIZACIÓN
 *
 *   perfil offline desbloqueado  =  usuario de la sesión en línea
 *
 * para CUALQUIER envío de la cola (manual, reconexión, primer plano, desbloqueo, temporizador de reintento, tras
 * crear una operación). Si no coinciden, no se envía ninguna operación: la cola queda intacta, con su propietario, y
 * nunca se reinterpreta como del otro usuario. El servidor lo comprobaría igual (cabecera `CABECERA_PERFIL_OFFLINE`,
 * `backend/src/api/identidad-perfil-offline.ts`), pero esto no depende de él.
 *
 * Se compara por `usuarios.id` —su huella SHA-256 (`perfil.ts::fingerprintDeUsuario`), la misma que identifica el
 * perfil—, nunca por email, rol u operario. La sesión la anuncia `contexto.tsx` (`fijarUsuarioDeSesion`) junto con el
 * token con el que se leyó: si el token cambia después (otra pestaña entra con otra cuenta) la identidad deja de
 * valer hasta que se vuelva a leer quién es.
 *
 * Única puerta: `sync.ts::ejecutarSincronizacion` (todo envío pasa por ahí) y, para no traer el paquete de otro
 * usuario a este perfil, `paquete.ts::prepararPaqueteOffline`.
 */
import { tokenGuardado } from '../api.ts';
import { fingerprintDeUsuario, perfilActivo } from './perfil.ts';

/** Cabecera con la huella del perfil offline que origina la petición: el servidor la rechaza si no es la de la sesión. */
export const CABECERA_PERFIL_OFFLINE = 'x-neatos-perfil-offline';
/** Mismo código que devuelve el servidor (409) cuando la huella no es la del usuario de la sesión. */
export const CODIGO_PERFIL_DE_OTRO_USUARIO = 'PERFIL_OFFLINE_DE_OTRO_USUARIO';
export const MENSAJE_PERFIL_DE_OTRO_USUARIO =
  'Este perfil pertenece a otro usuario. Inicia sesión con la cuenta correspondiente para sincronizar.';

let sesion: { readonly idUsuario: string; readonly token: string | null } | null = null;

/** La sesión en línea vigente (o `null` si no hay). La llama `contexto.tsx` cada vez que cambia el usuario. */
export function fijarUsuarioDeSesion(idUsuario: string | null): void {
  sesion = idUsuario === null ? null : { idUsuario, token: tokenGuardado() };
}

export type MotivoSinIdentidad = 'SIN_SESION' | 'PERFIL_BLOQUEADO' | 'OTRO_USUARIO';
export type IdentidadSync = { readonly ok: true; readonly comprobada: object } | { readonly ok: false; readonly motivo: MotivoSinIdentidad };

/** ¿Puede el perfil `idPerfil` enviar ahora con la sesión en línea? */
export async function comprobarIdentidadSync(idPerfil: string): Promise<IdentidadSync> {
  const s = sesion;
  if (s === null || tokenGuardado() !== s.token) return { ok: false, motivo: 'SIN_SESION' };
  const perfil = perfilActivo();
  if (perfil?.perfilId !== idPerfil) return { ok: false, motivo: 'PERFIL_BLOQUEADO' };
  const huella = await fingerprintDeUsuario(s.idUsuario);
  //  Lo que haya cambiado mientras se calculaba la huella también cuenta.
  if (sesion !== s || tokenGuardado() !== s.token) return { ok: false, motivo: 'SIN_SESION' };
  if (perfilActivo()?.perfilId !== idPerfil) return { ok: false, motivo: 'PERFIL_BLOQUEADO' };
  if (perfil.fingerprintUsuario !== huella) return { ok: false, motivo: 'OTRO_USUARIO' };
  return { ok: true, comprobada: s };
}

/** Justo antes de cada envío (síncrono): la sesión comprobada sigue siendo la vigente y su token no ha cambiado. */
export function identidadSigueVigente(c: { readonly comprobada: object }, idPerfil: string): boolean {
  return sesion !== null && sesion === c.comprobada && sesion.token === tokenGuardado() && perfilActivo()?.perfilId === idPerfil;
}

/** La cabecera para una petición de este perfil (vacía si no es el desbloqueado: entonces no se envía nada suyo). */
export function cabeceraPerfilOffline(idPerfil: string): Record<string, string> {
  const perfil = perfilActivo();
  return perfil?.perfilId === idPerfil ? { [CABECERA_PERFIL_OFFLINE]: perfil.fingerprintUsuario } : {};
}
